import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { ScreenLoader } from '@/components/global/Loader';
import { useTheme } from '@/components/providers/ThemeProvider';
import { supabaseClient } from '@/lib/supabase';

import { SignInForm } from './-components/SignInForm';

export const Route = createFileRoute('/(auth)/sign-in/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown> | undefined) =>
    z
      .object({
        source: z.string().optional(),
        userId: z.string().optional(),
      })
      .parse(search),
  loaderDeps: ({ search = {} }) => ({
    source: search.source,
    slackUserId: search.userId,
  }),
  loader: async ({ deps: { source, slackUserId } }) => {
    // Here, search is guaranteed to be at least an empty object.
    const isSlackFlow = source === 'slack';
    const {
      data: { session: alreadyLoggedInData },
    } = await supabaseClient.auth.getSession();

    if (alreadyLoggedInData !== null) {
      if (isSlackFlow) {
        throw redirect({
          to: `authorize/slack/${slackUserId}`,
        });
      }
      // Otherwise, redirect to the homepage.
      throw redirect({ to: '/' });
    }
  },
  pendingComponent: ScreenLoader,
});

function RouteComponent() {
  const { resolvedTheme } = useTheme();
  const search = Route.useSearch();
  const { source, userId } = search;
  const isSlackFlow = source === 'slack';

  return (
    <section className="bg-gray-50 dark:bg-gray-900">
      <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto md:h-screen lg:py-0">
        <div className="flex items-center mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
          <img
            className="mr-2"
            src={resolvedTheme === 'dark' ? '/LogoWhite.svg' : '/LogoBlack.svg'}
            alt="logo"
            width={100}
            height={100}
          />
        </div>
        <div className="w-full bg-white rounded-lg shadow dark:border md:mt-0 sm:max-w-md xl:p-0 dark:bg-gray-800 dark:border-gray-700">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-semibold leading-tight tracking-tight text-gray-900 md:text-2xl dark:text-white">
              <FormattedMessage id="auth.signIn.title" />
            </h1>
            <SignInForm isSlackFlow={isSlackFlow} slackUserId={userId} />
            <p className="text-sm font-light text-gray-500 dark:text-gray-400">
              <FormattedMessage id="auth.signIn.noAccount" />
              <Link
                to="/sign-up"
                className="font-medium hover:underline text-primary-600 dark:text-primary-500"
              >
                <FormattedMessage id="auth.signIn.signUp" />
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
